/**
 * 分享平台 - 客户浏览页面
 */
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, LogOut, Film, Image, Loader2, Heart, Star, MessageSquare, Bell, Check, ChevronDown } from 'lucide-react';
import { api, getErrorMessage } from '../services/api';
import { useToast } from '../components/Toast';
import { useAuth } from '../contexts/AuthContext';
import type { PromotionFile } from '../types/api';
import styles from './PromotionBrowse.module.css';

const CATEGORIES = ['基材类','油墨涂料类','印刷工艺类','功能类','智能化类','绿色可持续类','大满创新'];

function getFileIcon(t: string) {
  t = t.toLowerCase();
  if (['mp4','webm','mov','avi'].includes(t)) return <Film size={16} />;
  return <Image size={16} />;
}

export default function PromotionBrowse() {
  const { user, logout } = useAuth();
  const { showToast } = useToast();

  const [files, setFiles] = useState<PromotionFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [watchFilter, setWatchFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 12;

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getInviteeFiles();
      setFiles(res.data || []);
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  // Likes & Favorites (separate states)
  const [likedFiles, setLikedFiles] = useState<Set<number>>(new Set());
  const [favFiles, setFavFiles] = useState<Set<number>>(new Set());

  // Optimistic toggle
  const updateFileCount = (fid: number, field: 'like_count'|'fav_count'|'comment_count', delta: number) => {
    setFiles(prev => prev.map(f => f.file_id === fid ? {...f, [field]: Math.max(0, (f[field]||0) + delta)} : f));
  };

  const toggleLike = (fid: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setLikedFiles(prev => {
      const next = new Set(prev);
      const wasLiked = prev.has(fid);
      wasLiked ? next.delete(fid) : next.add(fid);
      updateFileCount(fid, 'like_count', wasLiked ? -1 : 1);
      return next;
    });
    api.request<{liked:boolean}>('/promotions/invitee/files/' + fid + '/like', { method: 'POST' }).catch(() => {});
  };

  const toggleFav = (fid: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setFavFiles(prev => {
      const next = new Set(prev);
      const wasFaved = prev.has(fid);
      wasFaved ? next.delete(fid) : next.add(fid);
      updateFileCount(fid, 'fav_count', wasFaved ? -1 : 1);
      return next;
    });
    api.request<{faved:boolean}>('/promotions/invitee/files/' + fid + '/fav', { method: 'POST' }).catch(() => {});
  };

  useEffect(() => {
    const likedIds = new Set<number>();
    const favedIds = new Set<number>();
    files.forEach(f => {
      if ((f as any).liked) likedIds.add(f.file_id);
      if ((f as any).faved) favedIds.add(f.file_id);
    });
    if (likedIds.size) setLikedFiles(prev => new Set([...prev, ...likedIds]));
    if (favedIds.size) setFavFiles(prev => new Set([...prev, ...favedIds]));
  }, [files]);

  const filtered = useMemo(() => {
    let r = files.filter(f => {
      const t = (f.title || f.file_name || '').toLowerCase();
      if (searchQuery && !t.includes(searchQuery.toLowerCase())) return false;
      if (watchFilter === 'watched' && !f.is_watched) return false;
      if (watchFilter === 'unwatched' && f.is_watched) return false;
      if (watchFilter === 'favorites' && !favFiles.has(f.file_id)) return false;
      if (categoryFilter && f.category !== categoryFilter) return false;
      return true;
    });
    r.sort((a, b) => new Date(b.create_time).getTime() - new Date(a.create_time).getTime());
    return r;
  }, [files, searchQuery, watchFilter, categoryFilter, likedFiles, favFiles]);

  const paged = filtered.slice((page-1)*pageSize, page*pageSize);
  const totalPages = Math.ceil(filtered.length / pageSize);

  // Hover
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  // Comments
  const [showComments, setShowComments] = useState(false);
  const [commentFile, setCommentFile] = useState<PromotionFile|null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [commentText, setCommentText] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);

  // 移动端工具栏悬浮
  const [showMobileToolbar, setShowMobileToolbar] = useState(false);
  // 留言板
  const [showMessages, setShowMessages] = useState(false);
  const [messages, setMessages] = useState<any[]>([]);
  const [msgContent, setMsgContent] = useState('');
  const [msgSending, setMsgSending] = useState(false);
  const [msgLoading, setMsgLoading] = useState(false);
  // 消息铃铛
  const [showBell, setShowBell] = useState(false);
  const bellRef = useRef<HTMLDivElement>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notiFilter, setNotiFilter] = useState('all');
  const [notiLoading, setNotiLoading] = useState(false);
  const [readIds, setReadIds] = useState<Set<number|string>>(() => {
    try { const raw=JSON.parse(localStorage.getItem('noti_read')||'[]'); if(raw.length&&typeof raw[0]==='number')return new Set(); return new Set(raw); } catch { return new Set(); }
  });
  const unreadCount = notifications.filter(n => !readIds.has(n.id)).length;
  const fetchNotifications = async () => {
    setNotiLoading(true);
    try { const r = await api.request<any[]>('/promotions/customer-messages'); setNotifications(r.data||[]); }
    catch { setNotifications([]); }
    finally { setNotiLoading(false); }
  };
  // 页面加载时自动拉取
  useEffect(() => { fetchNotifications(); }, []);
  const closeBell = () => {
    const ids = notifications.map((n:any) => n.id);
    if (ids.length > 0) {
      setReadIds(prev => { const next = new Set(prev); ids.forEach(id => next.add(id)); localStorage.setItem('noti_read', JSON.stringify([...next])); return next; });
    }
    setShowBell(false);
  };

  const fetchMessages = async () => {
    setMsgLoading(true);
    try { const r = await api.request<any[]>('/promotions/message-board'); setMessages(r.data||[]); } catch { setMessages([]); }
    finally { setMsgLoading(false); }
  };
  const sendMessage = async () => {
    if (!msgContent.trim()) return;
    setMsgSending(true);
    try {
      await api.request('/promotions/message-board', { method: 'POST', body: JSON.stringify({ content: msgContent }) });
      showToast('success', '留言已提交');
      setMsgContent(''); fetchMessages();
    } catch (e) { showToast('error', getErrorMessage(e)); }
    finally { setMsgSending(false); }
  };

  const openComments = async (f: PromotionFile, e: React.MouseEvent) => {
    e.stopPropagation();
    setCommentFile(f); setShowComments(true); setComments([]); setCommentLoading(true);
    try { const r = await api.request<any[]>('/promotions/invitee/files/' + f.file_id + '/comments'); setComments(r.data||[]); } catch { setComments([]); }
    finally { setCommentLoading(false); }
  };

  const handleCommentReply = async (cid: number) => {
    if (!replyText.trim() || !commentFile) return;
    try {
      await api.request(`/promotions/files/${commentFile.file_id}/comments/${cid}/reply`, { method:'PUT', body:JSON.stringify({reply:replyText}) });
      showToast('success', '回复成功');
      setReplyTextState(''); setReplyTo(null);
      const r = await api.request<any[]>('/promotions/invitee/files/' + commentFile.file_id + '/comments'); setComments(r.data||[]);
    } catch (e) { showToast('error', getErrorMessage(e)); }
  };

  const postComment = async () => {
    if (!commentFile || !commentText.trim()) return;
    setCommentLoading(true);
    try {
      await api.request('/promotions/invitee/files/' + commentFile.file_id + '/comments', { method:'POST', body:JSON.stringify({content:commentText}) });
      showToast('success', '评论提交成功'); setCommentText('');
      const r = await api.request<any[]>('/promotions/invitee/files/' + commentFile.file_id + '/comments'); setComments(r.data||[]);
    } catch(err) { showToast('error', getErrorMessage(err)); }
    finally { setCommentLoading(false); }
  };

  // Preview
  const [active, setActive] = useState<PromotionFile | null>(null);
  const viewStartRef = useRef<Date | null>(null);
  const timerRef = useRef<any>(null);
  const elapsedRef = useRef(0);

  const openFile = (f: PromotionFile) => {
    setActive(f);
    viewStartRef.current = new Date();
    elapsedRef.current = 0;
    timerRef.current = setInterval(() => { elapsedRef.current++; }, 1000);
  };

  const closeFile = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    const file = active;
    const start = viewStartRef.current;
    const duration = elapsedRef.current;
    setActive(null);
    if (start && file) {
      api.recordViewLog({ file_id: file.file_id, view_start: start.toISOString(), view_duration: duration }).catch(() => {});
      setFiles(prev => prev.map(f => f.file_id === file.file_id ? { ...f, is_watched: true } : f));
    }
  };

  return (
    <div className={styles.container}>
      {/* Topbar */}
      <div className={styles.topbar}>
        <div className={styles.brand}><h1>分享平台</h1><span>浏览推介内容</span></div>
        <div className={styles.userSection}>
          <button onClick={() => { setShowMessages(true); fetchMessages(); }} title="留言板" style={{display:'flex',alignItems:'center',gap:6,padding:'6px 14px',borderRadius:8,border:'1px solid var(--cinnabar-300)',background:'var(--cinnabar-50)',color:'var(--cinnabar-600)',cursor:'pointer',fontSize:'.8125rem',fontWeight:500,marginRight:12}}>
            <MessageSquare size={16} strokeWidth={1.5} /> 留言板
          </button>
          <div ref={bellRef} style={{position:'relative',marginRight:12}}>
            <button onClick={() => { if (showBell) closeBell(); else { setShowBell(true); fetchNotifications(); } }} title="消息" style={{background:'none',border:'none',cursor:'pointer',padding:8,color:'var(--ink-400)',borderRadius:10,position:'relative'}}>
              <Bell size={20} strokeWidth={1.5} />
              {unreadCount > 0 && <span style={{position:'absolute',top:2,right:2,background:'#ef4444',color:'#fff',fontSize:10,minWidth:18,height:18,borderRadius:9,display:'flex',alignItems:'center',justifyContent:'center',fontWeight:600}}>{unreadCount > 99 ? '99+' : unreadCount}</span>}
            </button>
            {showBell && <>
              <div onClick={closeBell} style={{position:'fixed',inset:0,zIndex:999}} />
              <div style={{position:'absolute',top:'100%',right:0,width:340,maxHeight:400,overflow:'auto',background:'#fff',borderRadius:14,boxShadow:'0 16px 48px rgba(0,0,0,.12)',border:'1px solid var(--ink-100)',zIndex:1000,marginTop:4}}>
                <div style={{padding:'14px 18px',fontWeight:600,fontSize:'.875rem',borderBottom:'1px solid var(--ink-100)',display:'flex',justifyContent:'space-between',position:'sticky',top:0,background:'#fff'}}>
                  <span>消息中心</span>
                  <button onClick={fetchNotifications} style={{background:'none',border:'none',cursor:'pointer',fontSize:'.75rem',color:'var(--ink-400)'}}>刷新</button>
                </div>
                <div style={{padding:'6px 14px',display:'flex',gap:6,borderBottom:'1px solid var(--ink-100)'}}>
                  {[{key:'all',label:'全部'},{key:'message_reply',label:'留言回复'},{key:'comment_reply',label:'评论回复'}].map(cat => {
                    const count = cat.key==='all' ? notifications.filter((n:any)=>!readIds.has(n.id)).length
                      : notifications.filter((n:any)=>n.type===cat.key&&!readIds.has(n.id)).length;
                    return (
                      <button key={cat.key} onClick={()=>setNotiFilter(cat.key)} style={{
                        padding:'4px 12px',borderRadius:16,border:'none',cursor:'pointer',fontSize:'.75rem',
                        background:notiFilter===cat.key?'var(--cinnabar-500)':'#f3f4f6',
                        color:notiFilter===cat.key?'#fff':'var(--ink-500)',
                        fontWeight:notiFilter===cat.key?600:400,
                        display:'flex',alignItems:'center',gap:4,
                      }}>
                        {cat.label}
                        {count>0&&<span style={{background:notiFilter===cat.key?'rgba(255,255,255,.3)':'#dc2626',color:'#fff',fontSize:10,minWidth:16,height:16,borderRadius:8,display:'inline-flex',alignItems:'center',justifyContent:'center'}}>{count>99?'99+':count}</span>}
                      </button>
                    );
                  })}
                </div>
                {notiLoading ? (
                  <div style={{padding:30,textAlign:'center',color:'var(--ink-300)',fontSize:'.8125rem'}}><Loader2 size={18} className={styles.spin} /> 加载中...</div>
                ) : (() => {
                  const filtered = notiFilter==='all' ? notifications : notifications.filter((n:any)=>n.type===notiFilter);
                  return filtered.length === 0 ? (
                  <div style={{padding:30,textAlign:'center',color:'var(--ink-300)',fontSize:'.8125rem'}}>暂无消息</div>
                ) : filtered.map((n: any, i: number) => {
                    const isUnread = !readIds.has(n.id);
                    return (
                  <div key={i} style={{padding:'12px 18px',borderBottom:'1px solid #f3f4f6',fontSize:'.8125rem',display:'flex',gap:10,alignItems:'flex-start',background:isUnread?'#dbeafe':'#f3f4f6',borderLeft:isUnread?'3px solid #3b82f6':'3px solid transparent'}}>
                    <div style={{width:28,height:28,borderRadius:'50%',background:n.type==='message_reply'?'#f0fdf4':n.type==='comment_reply'?'#eff6ff':'#f5f5f5',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                      {n.type==='message_reply' && <MessageSquare size={12} style={{color:'#16a34a'}}/>}
                      {n.type==='comment_reply' && <MessageSquare size={12} style={{color:'#2563eb'}}/>}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      {n.type==='comment_reply' && <>
                        <div style={{color:'var(--ink-600)',fontSize:'.8125rem'}}>评论：{n.content||''}</div>
                        {n.reply && <div style={{color:'#16a34a',fontSize:'.8125rem',marginTop:4}}>回复：{n.reply}</div>}
                      </>}
                      {n.type==='message_reply' && <>
                        <div style={{color:'var(--ink-600)',fontSize:'.8125rem'}}>留言：{n.content||''}</div>
                        {n.reply && <div style={{color:'#16a34a',fontSize:'.8125rem',marginTop:4}}>回复：{n.reply}</div>}
                      </>}
                      <div style={{display:'flex',alignItems:'center',gap:8,marginTop:6}}>
                        <span style={{color:'var(--ink-300)',fontSize:'.6875rem'}}>{new Date(n.create_time).toLocaleString('zh-CN')}</span>
                        {isUnread && <span style={{fontSize:'.625rem',color:'#3b82f6',background:'#dbeafe',padding:'1px 6px',borderRadius:3,fontWeight:500}}>未读</span>}
                      </div>
                    </div>
                  </div>
                );})})()}
              </div>
            </>}
          </div>
          <button onClick={logout} title="退出登录" className={styles.logoutBtn} style={{marginLeft:'auto'}}>
            <LogOut size={16} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Toolbar */}
      {/* ===== 桌面端工具栏 ===== */}
      <div className={styles.desktopToolbar}>
        <div className={styles.searchWrap}>
          <Search size={16} />
          <input placeholder="搜索视频..." value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setPage(1); }} />
          {searchQuery && <button onClick={() => { setSearchQuery(""); setPage(1); }}><X size={14} /></button>}
        </div>
        <div className={styles.filterBtns}>
          <button className={`${styles.filterBtn} ${watchFilter==='all'?styles.active:''}`} onClick={() => { setWatchFilter("all"); setPage(1); }}>全部 ({files.length})</button>
          <button className={`${styles.filterBtn} ${watchFilter==='unwatched'?styles.active:''}`} onClick={() => { setWatchFilter("unwatched"); setPage(1); }}>未观看 ({files.filter(f=>!f.is_watched).length})</button>
          <button className={`${styles.filterBtn} ${watchFilter==='watched'?styles.active:''}`} onClick={() => { setWatchFilter("watched"); setPage(1); }}>已观看 ({files.filter(f=>f.is_watched).length})</button>
          <button className={`${styles.filterBtn} ${watchFilter==='favorites'?styles.active:''}`} onClick={() => { setWatchFilter("favorites"); setPage(1); }}>⭐ 收藏 ({favFiles.size})</button>
        </div>
      </div>

      {/* ===== 移动端：搜索框 + 筛选按钮 同一行 ===== */}
      <div className={styles.mobileToolbarRow}>
        <div className={styles.searchWrap} style={{flex:1}}>
          <Search size={16} />
          <input placeholder="搜索视频..." value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setPage(1); }} />
          {searchQuery && <button onClick={() => { setSearchQuery(""); setPage(1); }}><X size={14} /></button>}
        </div>
        <button onClick={() => setShowMobileToolbar(true)} style={{background:'#fff',border:'1px solid var(--color-border)',borderRadius:8,padding:'8px 12px',cursor:'pointer',display:'flex',alignItems:'center',gap:4,fontSize:'.75rem',color:'var(--ink-500)',whiteSpace:'nowrap',flexShrink:0}}>
          筛选 <ChevronDown size={14} />
        </button>
      </div>

      {/* ===== 移动端悬浮筛选面板 ===== */}
      {showMobileToolbar && <div onClick={() => setShowMobileToolbar(false)} style={{position:'fixed',inset:0,zIndex:199}} />}
      {showMobileToolbar && (
        <div className={styles.mobilePanel}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
            <span style={{fontWeight:600,fontSize:'.875rem',color:'var(--ink-700)'}}>筛选</span>
            <button onClick={() => setShowMobileToolbar(false)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--ink-400)',padding:4}}><X size={18} /></button>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <span style={{fontSize:'.75rem',color:'var(--ink-400)',whiteSpace:'nowrap'}}>分类</span>
            <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1); }} style={{flex:1,padding:'6px 10px',borderRadius:6,border:'1px solid var(--ink-200)',fontSize:'.8125rem',background:'#fff'}}>
              <option value="">全部分类</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
            {[
              {k:'all',l:'全部',n:files.length},
              {k:'unwatched',l:'未观看',n:files.filter(f=>!f.is_watched).length},
              {k:'watched',l:'已观看',n:files.filter(f=>f.is_watched).length},
              {k:'favorites',l:'收藏',n:favFiles.size},
            ].map(({k,l,n}) => (
              <button key={k} onClick={() => { setWatchFilter(k); setPage(1); }}
                style={{flex:'1 1 calc(50% - 6px)',minWidth:100,padding:'8px 12px',borderRadius:8,border:watchFilter===k?'2px solid var(--cinnabar-500)':'1px solid var(--ink-200)',background:watchFilter===k?'var(--cinnabar-50)':'#fff',fontSize:'.75rem',fontWeight:watchFilter===k?600:400,color:watchFilter===k?'var(--cinnabar-600)':'var(--ink-500)',cursor:'pointer'}}>
                {l} <span style={{opacity:.6}}>{n}</span>
              </button>
            ))}
          </div>
          <button onClick={() => { setSearchQuery(''); setWatchFilter('all'); setCategoryFilter(''); setPage(1); setShowMobileToolbar(false); }}
            style={{marginTop:4,padding:'10px',borderRadius:8,border:'1px solid var(--ink-200)',background:'#f9fafb',fontSize:'.8125rem',cursor:'pointer',color:'var(--ink-400)',textAlign:'center'}}>
            清除所有筛选
          </button>
        </div>
      )}

      {/* Content */}
      <div className={styles.content}>
        {/* Category sidebar */}
        <div className={styles.sidebar}>
          <div className={styles.sidebarTitle}>视频分类</div>
          <button className={`${styles.catBtn} ${!categoryFilter?styles.catActive:''}`} onClick={() => { setCategoryFilter(""); setPage(1); }}>全部</button>
          {CATEGORIES.map(c => (
            <button key={c} className={`${styles.catBtn} ${categoryFilter===c?styles.catActive:''}`}
              onClick={() => { setCategoryFilter(categoryFilter === c ? "" : c); setPage(1); }}>{c}</button>
          ))}
        </div>

        <div className={styles.mainContent} style={{flex:1}}>
          {loading ? (
            <div style={{textAlign:'center',padding:60}}><Loader2 size={32} className={styles.spin} /></div>
          ) : paged.length === 0 ? (
            <div style={{textAlign:'center',padding:60,color:'#a3a3a3'}}>暂无内容</div>
          ) : (
            <div className={styles.grid}>
              {paged.map(f => (
                <motion.div key={f.file_id} className={styles.card} initial={{opacity:0,y:20}} animate={{opacity:1,y:0}}
                  onClick={() => openFile(f)}>
                  <div className={styles.cardCover} style={{position:'relative'}}
                    onMouseEnter={e => { e.stopPropagation(); setHoveredId(f.file_id); }}
                    onMouseLeave={e => { e.stopPropagation(); setHoveredId(null); }}>
                    {f.transcoded === false
                      ? <div className={styles.coverEmpty} style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:8}}><Loader2 size={28} className={styles.spin} /><span style={{fontSize:'.75rem',color:'var(--ink-400)'}}>处理中</span></div>
                      : ['mp4','webm','mov','avi'].includes((f.file_type||'').toLowerCase())
                      ? <video src={(f.preview_url||'').replace('/minio/','/internal-minio/')} poster={(f.poster_url||'').replace('/minio/','/internal-minio/')||undefined} muted loop playsInline className={styles.coverVideo} />
                      : ['jpg','jpeg','png','gif'].includes((f.file_type||'').toLowerCase())
                      ? <img src={(f.preview_url||'').replace('/minio/','/internal-minio/')} alt="" className={styles.coverImg} />
                      : <div className={styles.coverEmpty}>{getFileIcon(f.file_type||'')}</div>
                    }
                    {f.category && <span style={{position:'absolute',top:8,left:8,padding:'2px 8px',borderRadius:4,background:'rgba(0,0,0,.5)',color:'#fff',fontSize:'.6875rem',fontWeight:500,zIndex:2}}>{f.category}</span>}
                    <span style={{position:'absolute',top:8,right:8,padding:'2px 8px',borderRadius:4,fontSize:'.6875rem',fontWeight:500,zIndex:2,background:f.is_watched?'rgba(0,0,0,.3)':'rgba(197,48,48,.8)',color:'#fff'}}>{f.is_watched?'已观看':'未观看'}</span>
                    {f.description && hoveredId === f.file_id && (
                      <div style={{position:'absolute',inset:0,background:'rgba(0,0,0,.8)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',padding:20,fontSize:'.8125rem',lineHeight:1.6,textAlign:'center',zIndex:5}}>
                        {f.description}
                      </div>
                    )}
                  </div>
                  <div className={styles.cardBody}>
                    <h3>{f.title || f.file_name}</h3>
                    <div style={{display:'flex',marginTop:10,borderTop:'1px solid #f5f0ea'}} onClick={e => e.stopPropagation()}>
                      <button onClick={e => toggleLike(f.file_id, e)} title="点赞" style={{flex:1,background:'none',border:'none',cursor:'pointer',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:2,padding:'6px 0'}}>
                        <div style={{width:28,height:28,borderRadius:'50%',background:likedFiles.has(f.file_id)?'#fef2f2':'#f5f5f5',display:'flex',alignItems:'center',justifyContent:'center',transition:'all .15s'}}>
                          <Heart size={15} color={likedFiles.has(f.file_id)?'#dc2626':'#9ca3af'} fill={likedFiles.has(f.file_id)?'#dc2626':'none'}/>
                        </div>
                        <span style={{fontSize:'.6875rem',color:likedFiles.has(f.file_id)?'#dc2626':'var(--ink-400)'}}>{(f.like_count || 0) > 0 ? f.like_count : '点赞'}</span>
                      </button>
                      <button onClick={e => toggleFav(f.file_id, e)} title="收藏" style={{flex:1,background:'none',border:'none',cursor:'pointer',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:2,padding:'6px 0'}}>
                        <div style={{width:28,height:28,borderRadius:'50%',background:favFiles.has(f.file_id)?'#fffbeb':'#f5f5f5',display:'flex',alignItems:'center',justifyContent:'center'}}>
                          <Star size={15} color={favFiles.has(f.file_id)?'#f59e0b':'#9ca3af'} fill={favFiles.has(f.file_id)?'#f59e0b':'none'}/>
                        </div>
                        <span style={{fontSize:'.6875rem',color:favFiles.has(f.file_id)?'#f59e0b':'var(--ink-400)'}}>{(f.fav_count || 0) > 0 ? f.fav_count : '收藏'}</span>
                      </button>
                      <button onClick={e => openComments(f, e)} title="评论" style={{flex:1,background:'none',border:'none',cursor:'pointer',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:2,padding:'6px 0'}}>
                        <div style={{width:28,height:28,borderRadius:'50%',background:'#f5f5f5',display:'flex',alignItems:'center',justifyContent:'center'}}>
                          <MessageSquare size={15} color='#9ca3af'/>
                        </div>
                        <span style={{fontSize:'.6875rem',color:'var(--ink-400)'}}>{(f.comment_count || 0) > 0 ? f.comment_count : '评论'}</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{display:'flex',justifyContent:'center',gap:8,padding:'16px 0'}}>
              <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}
                style={{padding:'6px 14px',borderRadius:8,border:'1px solid var(--ink-200)',background:page===1?'#f5f5f5':'#fff',cursor:page===1?'default':'pointer',fontSize:'.8125rem'}}>上一页</button>
              <span style={{padding:'6px 12px',fontSize:'.8125rem',color:'var(--ink-500)'}}>{page} / {totalPages}</span>
              <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}
                style={{padding:'6px 14px',borderRadius:8,border:'1px solid var(--ink-200)',background:page===totalPages?'#f5f5f5':'#fff',cursor:page===totalPages?'default':'pointer',fontSize:'.8125rem'}}>下一页</button>
            </div>
          )}
        </div>
      </div>

      {/* Comment Modal */}
      <AnimatePresence>{showComments && commentFile && <motion.div key="comments" style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:24}} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setShowComments(false)}>
        <motion.div style={{width:'100%',maxWidth:560,maxHeight:'85vh',background:'#fff',borderRadius:14,display:'flex',flexDirection:'column'}} initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e=>e.stopPropagation()}>
          <div style={{padding:'14px 20px',fontWeight:600,borderBottom:'1px solid var(--ink-100)',display:'flex',justifyContent:'space-between'}}>
            <span>评论 · {commentFile.title||commentFile.file_name}</span>
            <button onClick={()=>setShowComments(false)} style={{background:'none',border:'none',cursor:'pointer'}}><X size={18}/></button>
          </div>
          <div style={{flex:1,overflow:'auto',padding:'12px 20px'}}>
            {commentLoading ? <div style={{textAlign:'center',padding:30,color:'var(--ink-300)',fontSize:'.8125rem'}}><Loader2 size={18} className={styles.spin} /> 加载中...</div> :
            comments.length===0 ? <div style={{textAlign:'center',padding:40,color:'var(--ink-300)',fontSize:'.8125rem'}}>暂无评论，快来抢沙发~</div> :
            comments.map((c:any)=>(
            <div key={c.id} style={{padding:'14px 0',borderBottom:'1px solid #f5f5f5'}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                <span style={{fontSize:'.8125rem',fontWeight:500,color:'#8b8b8b'}}>{c.user_name}</span>
                {c.user_id === user.user_id && <span style={{fontSize:'.625rem',color:'#3b82f6',background:'#dbeafe',padding:'1px 5px',borderRadius:3,fontWeight:500}}>我</span>}
                <span style={{fontSize:'.6875rem',color:'#bbb'}}>{new Date(c.create_time).toLocaleString('zh-CN')}</span>
              </div>
              <div style={{fontSize:'.875rem',color:'#222',lineHeight:1.6,wordBreak:'break-word'}}>{c.content}</div>
              {c.reply && (
                <div style={{marginTop:10,padding:'10px 14px',background:'#f8f8f8',borderRadius:8,fontSize:'.8125rem'}}>
                  <span style={{fontWeight:500,color:'#8b8b8b'}}>回复：</span>
                  <span style={{color:'#555'}}>{c.reply}</span>
                </div>
              )}
            </div>
            ))}
          </div>
          <div style={{padding:'12px 20px',borderTop:'1px solid var(--ink-100)',display:'flex',gap:8}}>
            <textarea value={commentText} onChange={e=>setCommentText(e.target.value)} placeholder="写评论..." rows={3}
              style={{flex:1,padding:'10px 14px',borderRadius:10,border:'1px solid var(--ink-200)',fontSize:'.875rem',resize:'none',fontFamily:'inherit',lineHeight:1.5}}
              onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();postComment()}}} />
            <button onClick={postComment} disabled={commentLoading}
              style={{padding:'8px 16px',borderRadius:8,border:'none',background:'var(--cinnabar-500)',color:'#fff',cursor:'pointer',fontSize:'.8125rem',alignSelf:'flex-end'}}>
              发送
            </button>
          </div>
        </motion.div>
      </motion.div>}</AnimatePresence>

      {/* 留言板弹窗 */}
      <AnimatePresence>{showMessages && <motion.div key="messages" style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:24}} initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setShowMessages(false)}>
        <motion.div style={{width:'100%',maxWidth:600,maxHeight:'85vh',background:'#fff',borderRadius:14,display:'flex',flexDirection:'column'}} initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e=>e.stopPropagation()}>
          <div style={{padding:'16px 20px',fontWeight:600,borderBottom:'1px solid var(--ink-100)',display:'flex',justifyContent:'space-between'}}>
            <span>💬 留言板</span>
            <button onClick={()=>setShowMessages(false)} style={{background:'none',border:'none',cursor:'pointer'}}><X size={18}/></button>
          </div>
          <div style={{flex:1,overflow:'auto',padding:'12px 20px',maxHeight:500}}>
            {msgLoading ? <div style={{textAlign:'center',padding:30,color:'var(--ink-300)',fontSize:'.8125rem'}}><Loader2 size={18} className={styles.spin} /> 加载中...</div> :
            messages.length===0 ? <div style={{textAlign:'center',padding:30,color:'var(--ink-300)',fontSize:'.8125rem'}}>暂无留言</div> :
            messages.map((m:any)=><div key={m.id} style={{padding:'12px 0',borderBottom:'1px solid #f5f5f5'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <span style={{display:'flex',alignItems:'center',gap:6}}>
                  <strong style={{fontSize:'.8125rem'}}>{m.user_name}</strong>
                  {m.user_id === user.user_id && <span style={{fontSize:'.625rem',color:'#3b82f6',background:'#dbeafe',padding:'1px 5px',borderRadius:3,fontWeight:500}}>我</span>}
                </span>
                <span style={{fontSize:'.6875rem',padding:'2px 8px',borderRadius:10,background:m.status===0?'#fef3c7':m.status===1?'#dcfce7':'#f1f5f9',color:m.status===0?'#92400e':m.status===1?'#166534':'#64748b'}}>{['未处理','已回复','已关闭'][m.status]}</span>
              </div>
              <div style={{fontSize:'.8125rem',color:'var(--ink-600)',marginTop:6,whiteSpace:'pre-wrap'}}>{m.content}</div>
              {m.reply && <div style={{marginTop:8,padding:'10px 14px',background:'#f0fdf4',borderRadius:8,fontSize:'.8125rem'}}>
                <span style={{fontWeight:600,color:'#166534'}}>回复（{m.replied_by}）：</span>
                <div style={{color:'var(--ink-600)',marginTop:4,whiteSpace:'pre-wrap'}}>{m.reply}</div>
              </div>}
              <div style={{fontSize:'.6875rem',color:'var(--ink-300)',marginTop:4}}>{new Date(m.create_time).toLocaleString('zh-CN')}</div>
            </div>)}
          </div>
          <div style={{padding:'12px 20px',borderTop:'1px solid var(--ink-100)',display:'flex',gap:8}}>
            <textarea value={msgContent} onChange={e=>setMsgContent(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey&&!e.nativeEvent.isComposing){e.preventDefault();const v=e.currentTarget.value.trim();if(!v)return;setMsgSending(true);api.request('/promotions/message-board',{method:'POST',body:JSON.stringify({content:v})}).then(()=>{showToast('success','留言已提交');setMsgContent('');fetchMessages()}).catch(e=>showToast('error',getErrorMessage(e))).finally(()=>setMsgSending(false));}}} placeholder="输入留言...（Enter 发送，Shift+Enter 换行）" rows={2}
              style={{flex:1,padding:'10px 14px',borderRadius:10,border:'1px solid var(--ink-200)',fontSize:'.875rem',resize:'none',fontFamily:'inherit',lineHeight:1.5}} />
            <button onClick={sendMessage} disabled={msgSending || !msgContent.trim()}
              style={{padding:'8px 20px',borderRadius:8,border:'none',background:'var(--cinnabar-500)',color:'#fff',cursor:'pointer',fontSize:'.8125rem',alignSelf:'flex-end',whiteSpace:'nowrap'}}>
              {msgSending ? '发送中...' : '发送'}
            </button>
          </div>
        </motion.div>
      </motion.div>}</AnimatePresence>

      {/* Preview Modal */}
      <AnimatePresence>
        {active && (
          <motion.div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.9)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center'}}
            initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={closeFile}>
            <motion.div style={{maxWidth:'95vw',maxHeight:'95vh'}} initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e => e.stopPropagation()}>
              <div style={{display:'flex',justifyContent:'flex-end',marginBottom:8}}>
                <button onClick={closeFile} style={{background:'none',border:'none',color:'#fff',cursor:'pointer'}}>
                  <X size={28} />
                </button>
              </div>
              {['mp4','webm','mov','avi'].includes((active.file_type||'').toLowerCase())
                ? <video src={active.preview_url} controls autoPlay playsInline controlsList="nodownload" onContextMenu={e=>e.preventDefault()} style={{maxWidth:'95vw',maxHeight:'85vh',borderRadius:8}} />
                : <img src={(active.preview_url||'').replace('/minio/','/internal-minio/')} alt="" style={{maxWidth:'95vw',maxHeight:'85vh',borderRadius:8}} />
              }
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
